'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download, ViewList, ViewModule } from '@mui/icons-material';
import { Button, TextField, Box, Grid, Card, CardContent, Typography, IconButton, ToggleButton, ToggleButtonGroup, FormControlLabel, Switch } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import {
  US_STATES,
  DEFAULT_NEW_LOCATION_COUNTRY,
  DEFAULT_NEW_LOCATION_SUBDIVISION,
} from '@/constants/form.constants';
import { useNavigate } from 'react-router-dom';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import * as Yup from 'yup';
import { fetchAllLocations, useCreateLocation } from '@/hooks/useLocation';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

const LocationsPage: React.FC = () => {

  const createLocationMutation = useCreateLocation();

  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const navigate = useNavigate();

  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  // Filter data based on search term and active status
  const filteredData = useMemo(() => {
    let filtered = data;
    
    // Filter by active status - hide inactive by default unless showInactive is true
    // Treat undefined/null as active (true) for backward compatibility
    if (!showInactive) {
      filtered = filtered.filter(location => {
        const isActive = location.active !== undefined && location.active !== null 
          ? location.active 
          : true; // Default to true if undefined/null
        return isActive !== false;
      });
    }
    
    // Filter by search term if provided
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(location => {
        const name = location.name?.toLowerCase() || '';
        const addressLine1 = location.addressLine1?.toLowerCase() || '';
        const city = location.city?.toLowerCase() || '';
        const subdivision = location.subdivision?.toLowerCase() || '';
        const postalCode = location.postalCode?.toLowerCase() || '';
        
        return (
          name.includes(searchLower) ||
          addressLine1.includes(searchLower) ||
          city.includes(searchLower) ||
          subdivision.includes(searchLower) ||
          postalCode.includes(searchLower)
        );
      });
    }
    
    return filtered;
  }, [data, search, showInactive]);

  // Function to get formatted address
  const getAddressString = (location: any) => {
    const { addressLine1, addressLine2, city, subdivision, postalCode, country } = location;
    return [addressLine1, addressLine2, city, subdivision, postalCode, country]
      .filter(Boolean)
      .join(', ');
  };

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    {
      accessorKey: 'name', header: 'Name', Cell: ({ row }: any) => {
        return <span style={{ cursor: 'pointer' }} onClick={() => { navigate('/app/locations/' + row?.original?.id) }}>{row?.original?.name}</span>
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        return <GenericActionPicker row={row.original} actions={[
          {
            icon: GENERIC_ICON_NAME.VIEW,
              label: "View Details",
              onClick: (row) => {
                navigate('/app/locations/' + row?.id)
              }
          },
          {
            icon: GENERIC_ICON_NAME.DELETE,
              label: "Delete",
              onClick: (row) => console.log("Deleting", row) // Custom delete action
          }
      ]} />
      },
    },
    { accessorKey: 'commissionPercentage', header: 'Com. %', 
      Cell: ({ row }: any) => {
        return <span>{Number(row?.original?.commissionPercentage).toFixed(2)}%</span>
      }
     },
    { accessorKey: 'goaCount', header: 'GOAs' },
    { accessorKey: 'trkCount', header: 'TRKs' },
    { accessorKey: 'atmCount', header: 'ATMs' },
    { accessorKey: 'ctmCount', header: 'CTMs' },
    { accessorKey: 'addressLine1', header: 'Address Line 1' },
    { accessorKey: 'city', header: 'City' },
    { accessorKey: 'subdivision', header: 'State' },
    { accessorKey: 'postalCode', header: 'Zip Code' },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations' },
  ];

  const handleExport = async () => {
    try {
      const rows = await fetchAllLocations();
      if (!rows.length) {
        alert('No data to export.');
        return;
      }
      const csvRows = [];
      const headers = Object.keys(rows[0]);
      csvRows.push(headers.join(','));
      for (const row of rows) {
        const values = headers.map(h => JSON.stringify(row[h] ?? ''));
        csvRows.push(values.join(','));
      }
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'locations.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export: ' + err);
    }
  };

  const rightContent = (
    <>
      <Button variant="outlined" startIcon={<Download />} sx={{ textTransform: 'none' }} onClick={handleExport}>
        Export
      </Button>
      <Button onClick={() => setCreateModalVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        + Add Location
      </Button>
    </>
  );

  // Shared location fields - same as LocationPage
  const createLocationFields = [{
    groupName: 'Location Details',
    fields: [
      { label: 'Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 12 },
      { label: 'Address Line 1', name: 'addressLine1', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
      { label: 'Address Line 2', name: 'addressLine2', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
      { label: 'City', name: 'city', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'State', name: 'subdivision', type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: false, columns: 6, options: US_STATES },
      { label: 'Zip Code', name: 'postalCode', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Country', name: 'country', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Commission Percentage', name: 'commissionPercentage', type: GENERIC_FORM_FIELD_TYPE.FLOAT, required: false, columns: 6, validation: Yup.number().min(0).max(100).transform((value) => (isNaN(value) ? undefined : value)) },
    ],
  }];

  // Fetch all locations for the table (no Redux)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const locations = await fetchAllLocations();
        setData(locations);
        setTotalRows(locations.length);
      } catch (err) {
        setData([]);
        setTotalRows(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [tableRefreshKey]);

  const renderLocationCard = (location: any) => (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={location.id}>
      <Card sx={{
        cursor: 'pointer',
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 4,
          transform: 'scale(1.03)',
        }
      }}
        onClick={() => navigate('/app/locations/' + location.id)}
      >
        <CardContent sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%'
        }}>
          <div>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 1 }}
            >
              {location.name}
            </Typography>
            
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                mb: 1
              }}
            >
              {getAddressString(location)}
            </Typography>

            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
              Commission: {Number(location.commissionPercentage || 0).toFixed(2)}%
            </Typography>
          </div>

          <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="textSecondary">
                GOAs: {location.goaCount || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                TRKs: {location.trkCount || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="caption" color="textSecondary">
                ATMs: {location.atmCount || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                CTMs: {location.ctmCount || 0}
              </Typography>
            </Box>

          </div>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <GenericFormModal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          createLocationMutation.mutate(values, {
            onSuccess: () => {
              setTableRefreshKey(tableRefreshKey + 1);
              setCreateModalVisible(false);
            },
            onError: (err) => {
              alert('Failed to create location: ' + String(err));
            }
          });
        }}
        initialValues={{
          active: true,
          country: DEFAULT_NEW_LOCATION_COUNTRY,
          subdivision: DEFAULT_NEW_LOCATION_SUBDIVISION,
        }}
        fieldGroups={createLocationFields}
        title={'Add Location'}
      />


      <TitleBar breadcrumbs={breadcrumbs} title="Locations" rightContent={rightContent} />

      <Box sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <TextField 
            placeholder="Search locations..." 
            variant="outlined" 
            size="small" 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            sx={{ width: '300px' }} 
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show Inactive"
          />
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newViewMode) => {
              if (newViewMode !== null) {
                setViewMode(newViewMode);
              }
            }}
            size="small"
          >
            <ToggleButton value="table">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === 'table' ? (
          <MaterialReactTable
            columns={columns}
            data={filteredData}
            manualPagination
            manualSorting
            enableGlobalFilter={false}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={filteredData.length}
            state={{ isLoading, pagination, sorting }}
            initialState={{ density: 'compact' }}
          />
        ) : (
          <Grid container spacing={2}>
            {filteredData.map(renderLocationCard)}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default LocationsPage;
